import React, { useState, useRef, ChangeEvent } from 'react';
import {
  getImageValidationError,
  createImagePreview,
  formatFileSize,
} from '../../utils/imageUtils';

interface ImageUploadProps {
  value?: string;
  onChange: (file: File | null, preview?: string) => void;
  className?: string;
  accept?: string;
  maxSize?: number;
  placeholder?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  className = '',
  accept = 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
  maxSize = 5 * 1024 * 1024, // 5MB
  placeholder = 'Click to upload image',
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | undefined>(value);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setPreview(value);
  }, [value]);

  const validateAndProcessFile = async (file: File): Promise<void> => {
    setError(null);

    // Validate file
    const validationError = getImageValidationError(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsUploading(true);

      // Create preview
      const previewUrl = await createImagePreview(file);
      setPreview(previewUrl);

      // Pass file and preview to parent
      onChange(file, previewUrl);
    } catch (err) {
      setError('Failed to process image. Please try again.');
      console.error('Image processing error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await validateAndProcessFile(file);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      await validateAndProcessFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    setPreview(undefined);
    onChange(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        className={`
          relative
          border-2 border-dashed
          rounded-2xl
          p-4
          cursor-pointer
          transition-colors
          duration-200
          ${
            isDragging
              ? 'border-brand-primary bg-brand-primary/5'
              : disabled
              ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
              : 'border-slate-300 hover:border-brand-primary hover:bg-brand-primary/5'
          }
        `}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          disabled={disabled}
          className="hidden"
        />

        {preview ? (
          <div className="space-y-2">
            <img
              src={preview}
              alt="Preview"
              className="mx-auto h-32 w-32 rounded-2xl object-cover shadow-soft"
            />
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                disabled={disabled}
                className="px-3 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary/10 rounded-full transition-colors"
              >
                Change Image
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                disabled={disabled}
                className="px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-full transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-2">
            {isUploading ? (
              <div className="flex justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-primary border-t-transparent"></div>
              </div>
            ) : (
              <div className="flex justify-center">
                <svg
                  className="h-8 w-8 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-600">{placeholder}</p>
              <p className="text-xs text-slate-400">
                PNG, JPG, WebP up to {formatFileSize(maxSize)}
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;